import React, { FunctionComponent } from "react";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/RenderType";
import {
    colorTwoBright,
    colorTwoDark,
} from "../../../../globalStyles/colors/scoutColors";
import { IGPSData } from "../../../../Models/IGPSData";
import { HH_ATTR_STATIC_TEXT } from "../../../../Util/ScoutDigitalShareUtil";
import {
    formatEducation,
    formatUYN,
    ReportChartGroup,
    sortDwellingType,
    sortEducation,
    sortMergedChartData,
    sortUYN,
    formatDwelling,
    ReportH4,
    formatMarriedSingle,
    ThreeColumnGrid,
} from "../../../Common";
import { CHILD_HEIGHT } from "../../../Common/DigitalShare/DigitalShareGenerator";
import { mergeGPSData } from "../../RetailMatch/Logic/SharedMethods";

interface IHHCharChartChartsProps {
    storeData: IGPSData;
    location1Name: string;
    benchmarkData?: IGPSData;
    location2Name?: string;
    renderType: RenderType;
}

export const getChartText = (
    visits: boolean,
    location1Name: string,
    location2Name?: string
): JSX.Element => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
            }}
        >
            <span style={{ marginBottom: 5, fontWeight: 900 }}>
                Household characteristics of {visits ? "Visits" : "Visitors"} to{" "}
                {location1Name}
                {location2Name ? (
                    <>
                        <span
                            data-testid="GPS-HH-chart-text-COMPARED-TO"
                            style={{ fontWeight: 400 }}
                        >
                            {" "}
                            COMPARED TO{" "}
                        </span>{" "}
                        {location2Name}{" "}
                    </>
                ) : null}
                .
            </span>
            <span>{HH_ATTR_STATIC_TEXT}</span>
        </div>
    );
};

const HHCharChartCharts: FunctionComponent<IHHCharChartChartsProps> = (
    props: IHHCharChartChartsProps
) => {
    const { storeData, benchmarkData, location1Name, location2Name } = props;

    return (
        <>
            <ReportH4 style={{ lineHeight: "normal", marginTop: "75px" }}>
                Household characteristics of {location1Name}
                {location2Name && ` COMPARED TO ${location2Name}`}
            </ReportH4>
            <p style={{ fontSize: "17px" }}>{HH_ATTR_STATIC_TEXT}</p>
            <ThreeColumnGrid>
                <ReportChartGroup
                    title="Marriage Status"
                    tooltipText={`"Married" households index [marriageSel1NatHigherLower] the national average at [marriageSel1Value] vs [marriageNationalValue]`}
                    tooltipText2={`“Married” households index [marriageSel1Sel2HigherLower] the ${location2Name} locations at [marriageSel1Value] vs. [marriageSel2Value].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.maritalStatus,
                            selection2:
                                benchmarkData?.actualConsumerViewRollups.maritalStatus,
                            national: storeData.nationalConsumerViewRollups.maritalStatus,
                        })
                    )}
                    xTickFormatter={formatMarriedSingle}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.Marriage}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Children"
                    tooltipText={`Households with children index [childrenSel1NatHigherLower] the national average at [childrenSel1Value] vs [childrenNationalValue]`}
                    tooltipText2={`Households with children index [childrenSel1Sel2HigherLower] the ${location2Name} locations at [childrenSel1Value] vs. [childrenSel2Value].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.childrenPresent,
                            selection2:
                                benchmarkData?.actualConsumerViewRollups.childrenPresent,
                            national: storeData.nationalConsumerViewRollups.childrenPresent,
                        }),
                        false,
                        sortUYN
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    xTickFormatter={formatUYN}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.Children}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Number of Persons in Household"
                    tooltipText="Households with [sel1NatTopId] [personPeopleSel1Nat] index the highest vs. the national average at [sel1NatCompSel1TopValue] vs [sel1NatCompNatTopValue]."
                    tooltipText2={`Households with [sel1Sel2TopId] [personPeopleSel1Sel2] index the highest vs. the ${location2Name} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.numPersonsInHh,
                            selection2:
                                benchmarkData?.actualConsumerViewRollups.numPersonsInHh,
                            national: storeData.nationalConsumerViewRollups.numPersonsInHh,
                        })
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.NumberOfPersons}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Head of Household Age"
                    tooltipText="Households with head of household age [sel1NatTopId] index the highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                    tooltipText2={`Households with head of household age [sel1Sel2TopId] index the highest vs. ${location2Name} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.hohAge,
                            selection2: benchmarkData?.actualConsumerViewRollups.hohAge,
                            national: storeData.nationalConsumerViewRollups.hohAge,
                        }),
                        true
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.HeadOfHouseholdAge}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Head of Household Ethnicity"
                    tooltipText="[sel1NatTopId] households index the highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                    tooltipText2={`[sel1Sel2TopId] households index the highest vs. the ${location1Name} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.hohEthnicGroup,
                            selection2:
                                benchmarkData?.actualConsumerViewRollups.hohEthnicGroup,
                            national: storeData.nationalConsumerViewRollups.hohEthnicGroup,
                        }),
                        true
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    makeXLabelsFitDemmit={true}
                    xAxisHeight={100}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.RaceAndEthnicity}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Household Income"
                    tooltipText="Households with income [sel1NatTopId] index the highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                    tooltipText2={`Households with income [sel1Sel2TopId] index the highest vs. the ${location2Name} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.hhIncome,
                            selection2: benchmarkData?.actualConsumerViewRollups.hhIncome,
                            national: storeData.nationalConsumerViewRollups.hhIncome,
                        }),
                        true
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    makeXLabelsFitDemmit={true}
                    xAxisHeight={90}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.HouseholdIncome}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Household Education Level"
                    tooltipText="Households with a [sel1NatTopId] index highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                    tooltipText2={`Households with a [sel1Sel2TopId] index the highest vs. the ${location2Name} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.hohEducationLevel,
                            selection2:
                                benchmarkData?.actualConsumerViewRollups.hohEducationLevel,
                            national: storeData.nationalConsumerViewRollups.hohEducationLevel,
                        }),
                        false,
                        sortEducation
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    xTickFormatter={formatEducation}
                    makeXLabelsFitDemmit={true}
                    xAxisHeight={100}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.HouseholdEducationLevel}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Home Type"
                    tooltipText={`The greatest % of households observed at ${location1Name} live in [greatestSel1Id] dwelling units at [greatestSel1Value]. Households in a single family dwelling unit index [dwellingTypeSel1NatHigherLower] the national average of [dwellingTypeNatValue].`}
                    tooltipText2={`Households in single family dwelling units index [dwellingTypeSel1Sel2HigherLower] than the ${location2Name} locations ([greatestSel1CompSel2Value]).`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.dwellingType,
                            selection2: benchmarkData?.actualConsumerViewRollups.dwellingType,
                            national: storeData.nationalConsumerViewRollups.dwellingType,
                        }),
                        false,
                        sortDwellingType
                    )}
                    xTickFormatter={formatDwelling}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.DwellingType}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="Home Ownership"
                    tooltipText={`The greatest % of households observed at ${location1Name} [greatestOwnSel1Id] their home at [greatestSel1Value]. Households who own index [ownSel1NatHigherLower] the national average of [ownNationalValue].`}
                    tooltipText2={`Households who own index [ownSel1Sel2HigherLower] than the ${location2Name} locations ([ownSel2Value]).`}
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.homeOwnership,
                            selection2:
                                benchmarkData?.actualConsumerViewRollups.homeOwnership,
                            national: storeData.nationalConsumerViewRollups.homeOwnership,
                        })
                    )}
                    initialYValue="selection1PrimaryWeight"
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.HomeOwnership}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="New Homeowner"
                    tooltipText="[newOwnerSel1Value] of households are new homeowners. New homeowner households index [newOwnerSel1NationalHigherLower] the national average of [newOwnerNationalValue]."
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.newHomeOwner,
                            selection2: benchmarkData?.actualConsumerViewRollups.newHomeOwner,
                            national: storeData.nationalConsumerViewRollups.newHomeOwner,
                        }),
                        false,
                        sortUYN
                    )}
                    initialYValue="selection1PrimaryWeight"
                    xTickFormatter={formatUYN}
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.NewHomeOwner}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
                <ReportChartGroup
                    title="New Mover"
                    tooltipText="[newMoverSel1Value] of households are new movers. New Mover Households index [newMoverSel1NationalHigherLower] the national average at [newMoverNationalValue]."
                    data={sortMergedChartData(
                        mergeGPSData({
                            selection1: storeData.actualConsumerViewRollups.newMover,
                            selection2: benchmarkData?.actualConsumerViewRollups.newMover,
                            national: storeData.nationalConsumerViewRollups.newMover,
                        }),
                        false,
                        sortUYN
                    )}
                    initialYValue="selection1PrimaryWeight"
                    xTickFormatter={formatUYN}
                    primaryColor={colorTwoBright}
                    secondaryColor={colorTwoDark}
                    primaryWeightLabel="Visitors"
                    secondaryWeightLabel="Visits"
                    scrollableParentSelector="#GPSReportBody"
                    chartAnim="zoomIn"
                    selection1Name={location1Name}
                    selection2Name={location2Name}
                    app={DigitalShareAppType.Mobilytics}
                    itemType={DigitalShareItemType.NewMover}
                    renderType={props.renderType}
                    shareText={(visits: boolean) =>
                        getChartText(visits, location1Name, location2Name)
                    }
                    chartHeight={
                        props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                    }
                />
            </ThreeColumnGrid>
        </>
    );
};

export default HHCharChartCharts;
